import { useState } from "react";

import { Ellipsified } from "metabase/core/components/Ellipsified";
import { useDispatch } from "metabase/lib/redux";
import { isNotNull } from "metabase/lib/types";
import {
  fetchCardParameterValues,
  fetchDashboardParameterValues,
  fetchParameterValues,
} from "metabase/parameters/actions";
import ParameterFieldWidgetValue from "metabase/parameters/components/widgets/ParameterFieldWidget/ParameterFieldWidgetValue/ParameterFieldWidgetValue";
import { formatParameterValue } from "metabase/parameters/utils/formatting";
import type Question from "metabase-lib/v1/Question";
import type { UiParameter } from "metabase-lib/v1/parameters/types";
import {
  getFields,
  hasFields,
  isFieldFilterUiParameter,
} from "metabase-lib/v1/parameters/utils/parameter-fields";
import {
  isDateParameter,
  isStringParameter,
} from "metabase-lib/v1/parameters/utils/parameter-type";
import { parameterHasNoDisplayValue } from "metabase-lib/v1/parameters/utils/parameter-values";
import type { Dashboard, ParameterValue, RowValue } from "metabase-types/api";

export type FormattedParameterValueProps = {
  dashboard?: Dashboard | null;
  question?: Question | null;
  parameters?: UiParameter[];
  parameter: UiParameter;
  value: string | number | number[];
  placeholder?: string;
  isPopoverOpen?: boolean;
};

function FormattedParameterValue({
  dashboard,
  question,
  parameters,
  parameter,
  value,
  placeholder,
  isPopoverOpen = false,
}: FormattedParameterValueProps) {
  const [asyncLabel, setAsyncLabel] = useState<any>(null);
  const dispatch = useDispatch();
  if (parameterHasNoDisplayValue(value)) {
    return placeholder;
  }

  const first = getValue(value);
  const values = parameter?.values_source_config?.values;
  const displayValue = values?.find(
    value => getValue(value)?.toString() === first?.toString(),
  );

  const label = getLabel(displayValue);
  if (Array.isArray(value) && (value as any).length === 1) {
    if (parameter.values_query_type === "cascader") {
      const deptOptions = (window as any).Metabase.store.getState().app
        .tempStorage.deptOptions;
      const labelValue = recursionLabel(first, deptOptions);
      if (labelValue !== asyncLabel) {
        setAsyncLabel(labelValue);
      }
    } else if (parameter.values_source_config?.isKeyValue) {
      const dispatchFetchParameterValues = async (query?: string) => {
        if (!parameter) {
          return { has_more_values: false, values: [] };
        }

        return dispatch(
          fetchParameterValues({
            parameter,
            query,
          }),
        );
      };

      const dispatchFetchCardParameterValues = async (query?: string) => {
        const cardId = question?.id();

        if (!isNotNull(cardId) || !parameter) {
          return { has_more_values: false, values: [] };
        }

        return dispatch(
          fetchCardParameterValues({
            cardId,
            parameter,
            query,
          }),
        );
      };

      const dispatchFetchDashboardParameterValues = async (query?: string) => {
        const dashboardId = dashboard?.id;

        if (!isNotNull(dashboardId) || !parameter || !parameters) {
          return { has_more_values: false, values: [] };
        }

        return dispatch(
          fetchDashboardParameterValues({
            dashboardId,
            parameter,
            parameters,
            query,
          }),
        );
      };
      const fetchValues = async () => {
        let newOptions: any[] = [];
        try {
          if (dashboard?.id) {
            const { values } = await dispatchFetchDashboardParameterValues();
            newOptions = values;
          } else if (question?.isSaved()) {
            const { values } = await dispatchFetchCardParameterValues();
            newOptions = values;
          } else if (parameter != null) {
            const { values } = await dispatchFetchParameterValues();
            newOptions = values;
          }
        } finally {
          newOptions =
            newOptions?.map(l => (l[0] as string)?.split(",") as any) || [];
        }
        const optionItem = newOptions.find(l => l[0] === first) || [];
        const labelValue = optionItem[1] || "";
        if (labelValue !== asyncLabel) {
          setAsyncLabel(labelValue);
        }
      };
      fetchValues();
    }
  } else {
    if (asyncLabel !== null) {
      setAsyncLabel(null);
    }
  }
  const renderContent = () => {
    if (asyncLabel || label) {
      return (
        <span>{formatParameterValue(asyncLabel || label, parameter)}</span>
      );
    }
    if (
      isFieldFilterUiParameter(parameter) &&
      hasFields(parameter) &&
      !isDateParameter(parameter)
    ) {
      return (
        <ParameterFieldWidgetValue
          fields={getFields(parameter)}
          value={value}
          displayValue={label}
        />
      );
    }
    return <span>{formatParameterValue(value, parameter)}</span>;
  };

  if (isStringParameter(parameter)) {
    const hasLongValue = typeof first === "string" && first.length > 80;
    return (
      <Ellipsified
        showTooltip={!isPopoverOpen}
        multiline
        tooltipMaxWidth={hasLongValue ? 450 : undefined}
      >
        {renderContent()}
      </Ellipsified>
    );
  }

  return renderContent();
}

function getValue(
  value: string | number | number[] | ParameterValue | undefined,
): RowValue | undefined {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value?.toString();
}

function getLabel(
  value: string | ParameterValue | undefined,
): string | undefined {
  if (Array.isArray(value)) {
    return value[1];
  }
  return value?.toString();
}
function recursionLabel(value: any, arr: Array<any>): any {
  let label;
  if (arr?.length) {
    for (let i = 0; i < arr.length; i++) {
      const element = arr[i];
      if (element.value === value) {
        label = element.label;
      } else if (element.children?.length) {
        label = recursionLabel(value, element.children);
      }
      if (label) {
        break;
      }
    }
  }
  return label;
}
// eslint-disable-next-line import/no-default-export -- deprecated usage
export default FormattedParameterValue;
